import React from 'react'
import { AutofitIFrame } from '@ta-interaktiv/react-autofit-iframe'
import { Link } from '@ta-interaktiv/contentbricks'
import { StepChart } from 'components/StepChart'
import { Box } from 'components/Box'

export const TEXT_FR = {
  title: <>Comment la Suisse s’en est tirée face à ses voisins</>,
  lead: (
    <>
      Comparé à la France ou l’Allemagne, notre pays a connu la surmortalité la
      plus importante durant la deuxième vague. Mais c’est aussi le pays qui a
      le moins fermé ses écoles. Notre grand bilan en sept points.
    </>
  ),
  updated: <>Mis à jour le 11 juin, 2021</>,
  propsinkey: {
    excessmort: 'Surmortalité',
    tests: 'Tests de dépistage',
    vacc: 'Vaccination',
    sequenced: 'Séquençage ADN',
    gdp: 'Économie',
    stringency: 'Restrictions',
    schools: 'Écoles'
  },
  better: 'meilleur,  plus ouvert',
  worse: 'moins bon,  davantage de restrictions',
  ave: 'Moyenne',
  swiss: 'Suisse',
  credits: ['Analyse des données et texte', 'Graphiques et visualisation'],
  questions: [
    'Ce contenu était-il informatif?',
    'La mise en forme vous plaît-elle?'
  ],
  intro: (
    <>
      <p>
        Le Conseil fédéral devrait mettre en consultation ce vendredi 11 juin la
        prochaine et dernière étape d’ouverture. Le gouvernement a des raisons
        d’être optimiste: grâce aux vaccins, les hôpitaux se vident, les jours
        sans annonces de décès se multiplient. L’heure du bilan peut commencer.
        Pour le ministre de la Santé Alain Berset, «la Suisse ne s’en tire pas
        si mal par rapport à ses voisins», comme il l’a affirmé il y a quelques
        jours dans l’émission Arena de la télévision alémanique. Ou encore
        d’assurer, lors d’une l’émission sur internet à l’attention des jeunes,
        qu’Emmanuel Macron a «moins bien géré la crise que lui». Sans la moindre
        seconde d’hésitation.
      </p>
      <p>
        Ce qu’Alain Berset ne dit pas, c’est qu’au cours de la deuxième vague,
        la Suisse est au côté du Portugal le pays qui a connu la surmortalité
        plus importante de l’ouest européen. Et que Confédération avait fait
        pâle figure en ce qui concerne les masques et les tests durant la
        première vague.
      </p>
      <p>
        Alors, bonne ou mauvaise élève, la Suisse? C’est ce que nous avons
        cherché à comprendre, en comparant sept indicateurs, du nombre de morts
        à la fermeture ou non des écoles.
      </p>
      <h4>Le résultat</h4>
    </>
  ),
  visual: (
    <>
      {' '}
      <p>
        La comparaison sur ces sept points montre que la Suisse n’a vraiment pas
        de quoi avoir honte. Contrairement à la plupart des pays voisins, on y a
        largement laissé tourner l'économie, les libertés de réunion et de
        mouvement ont été moins restreintes et les établissements scolaires ont
        davantage été maintenus ouverts que dans d'autres pays. Les trois
        cercles à droite du graphique montrent ces indicateurs. Là, la Suisse se
        trouve dans la zone bleue, ce qui signifie qu'elle fait mieux que la
        moyenne des douze pays. En revanche, elle ne s’est pas montrée
        particulièrement leste en ce qui concerne les tests et les vaccinations
        (les deux bulles rouges à gauche). La bulle au centre des graphiques de
        chaque pays représente la surmortalité. Grâce à une première vague
        passée sans trop d'accroc, surtout en Suisse alémanique, cette valeur
        n'est pas si mauvaise non plus.
      </p>
    </>
  ),
  categories: (
    <>
      <Box title='La méthode?'>
        <p>
          Nous avons d’abord choisi de mesurer la Suisse avec ses voisins. Une
          comparaison avec la Nouvelle-Zélande ou la Corée du Sud n’est en effet
          pas pertinente, car la dynamique épidémique n’était pas la même.
        </p>
        <p>
          Les critères que nous avons sélectionnés couvrent une palette aussi
          large que possible de facteurs, de la santé des gens à l’économie, en
          passant par le contrôle de l’épidémie. Et il n’y a délibérément pas de
          classement final: chacune et chacun choisira les critères qui lui
          semblent les plus importants.
        </p>
      </Box>

      <p>
        C’est un très bon moment pour tirer un premier bilan, estime Antoine
        Flahault, directeur de l’Institut de santé globale de la Faculté de
        médecine de l’Université de Genève. Proche de l’OMS, le professeur est
        spécialisé dans l’analyse des politiques de santé publique: «Il me
        semble important de faire ce bilan en considérant les différents aspects
        économiques, sociétaux et sanitaires, exactement comme vous le faites
        ici.» (<Link href='878721290546'>Lire l’interview</Link>).
      </p>
      <p>
        Pour Emma Hodcroft, épidémiologiste à l’Université de Berne, il est
        également important de faire un bilan sur la durée, sur toute la
        dynamique de l’épidémie, afin se faire une idée vraiment précise de la
        situation. «Certains pays ont pris de bonnes décisions, explique-t-elle,
        puis ont fait des erreurs, et enfin ont de nouveau fait de bons choix.»
      </p>
      <p>
        Même si la Suisse est en bonne position sur de nombreux points, un
        examen des différents critères montre qu'elle ne le doit pas seulement à
        sa politique. Parfois, c'était aussi dû à des circonstances heureuses,
        ou à une aide extérieure.
      </p>
    </>
  ),
  excessmort: (
    <>
      <p>
        Sur l’ensemble de la pandémie, l’Italie, la Belgique, le Portugal, la
        Grande-Bretagne ont tous fait moins bien que la Suisse. Et l’Espagne
        ferme la marche, avec 19% de morts de plus qu’en temps normal. En
        Suisse, cet excédent de décès était de 12%. Soit près du double qu’en
        Allemagne (6,3%) et sept fois plus qu’au Danemark (seulement 1,6% de
        morts en plus que d’habitude).
      </p>
      <p>
        Si l’on regarde l'évolution de la surmortalité au fil du temps, c’est
        encore plus patent: au cours de la première vague, l’Italie et la France
        ont été davantage touchées que notre pays, parce que ces deux pays ont
        simplement eu la malchance que le virus ait été importé là. Mais lors de
        la deuxième vague, quand on pouvait vraiment se préparer, c’est la
        Suisse qui a payé le plus lourd tribut. Comme si elle avait{' '}
        <Link href='435977821269'>moins bien tiré les leçons</Link> que les
        autres.
      </p>
      <AutofitIFrame src='https://datawrapper.dwcdn.net/dIOVX/' />
      <p>
        «A la fin de l'été et en automne, explique Christian Althaus,
        épidémiologiste à l'Université de Berne, le gouvernement a longtemps
        hésité à réagir par des mesures. Cela a conduit à un nombre très élevé
        de décès en novembre et décembre, qui aurait pu être évité avec une
        introduction un peu plus rapide des limites pour les rassemblements et
        les événements.»
      </p>
      <p>
        La Suisse, aurait pu faire beaucoup mieux: en plus d’avoir l’un des
        systèmes de santé les plus performants au monde, elle est l’un des pays
        d’Europe qui compte la moins grande proportion de personnes de plus de
        65 ans, la catégorie où la plupart des décès liés à la pandémie ont eu
        lieu.
      </p>
      <p>
        Lire aussi:{' '}
        <Link href='435977821269'>
          Deuxième vague – le drame s’est joué au cœur de l’été
        </Link>
      </p>
    </>
  ),
  tests: (
    <>
      <p>
        Les tests de dépistage Covid-19 sont essentiels pour briser les chaînes
        d’infection et ralentir l’épidémie. Seuls le Danemark, l’Autriche et la
        Grande-Bretagne se détachent du reste de l’Europe en nombre de tests par
        habitant. La Suisse, elle, se place en queue du peloton.
      </p>
      <p>
        Pourtant, en mars 2020 déjà, un groupe de treize scientifiques avait
        <Link href='https://smw.ch/article/doi/smw.2020.20225'>
          souligné l’importance
        </Link>{' '}
        d’une stratégie intensive de dépistage pour la Suisse.
      </p>
      <p>
        Seulement voilà, le Conseil fédéral et l’OFSP ont mis plus de trois mois
        avant de présenter un plan pour la gratuité totale des tests. Et encore
        jusqu’en avril 2021 pour permettre les auto-tests gratuits pour toute la
        population. L’Autriche avait fait de même, mais à chaque fois avec
        plusieurs semaines d’avance sur la Suisse.
      </p>
      <AutofitIFrame src='https://datawrapper.dwcdn.net/zzcdp/' />
      <p>
        Pour voir si un pays a assez ou trop peu testé, explique le virologue de
        l’EPFL Didier Trono, on doit surtout observer le nombre de tests
        positifs pour 100 tests effectués. Plus cette valeur est élevée, plus on
        a perdu le contrôle de l’épidémie. En novembre 2020, alors qu’elle ne
        pouvait plus invoquer la surprise et le manque de réactifs comme lors de
        la première vague, la Suisse affichait un taux de positivité de de 27%,
        contre 15% en France et en Italie et 8% en Allemagne.
      </p>
      <AutofitIFrame src='https://datawrapper.dwcdn.net/mk3PU/' />
      <p>
        Quant au Danemark, note le professeur Flahault, ce taux était à moins de
        2%. Il est l’un des trois pays européens avec la Norvège et la Finlande
        à avoir poursuivi une stratégie de «suppression» du virus. Ailleurs, on
        a appliqué une politique de «mitigation», c’est-à-dire la limitation de
        sa circulation dans le but de préserver le système de santé.
      </p>
    </>
  ),
  vacc: (
    <>
      <p>
        Si, depuis quelques jours, la Suisse vaccine le plus de personnes pour
        100 habitants en Europe, elle n’a toujours pas rattrapé ses voisins, et
        encore moins la Grande-Bretagne.
      </p>
      <AutofitIFrame src='https://datawrapper.dwcdn.net/wTEvT/' />
      <p>
        Cette dernière a très tôt misé sur le vaccin d’AstraZeneca. Moins cher
        et plus facile à produire puis à distribuer, il n’est pas non disponible
        en Suisse. En plus, la Grande-Bretagne a décidé d’espacer au maximum le
        temps entre la première et la seconde dose, afin d’atteindre une
        immunité vaccinale le plus rapidement possible. Cette stratégie semble
        s’avérer payante. Mais elle est risquée, car certains variants, plus
        contagieux, pourraient passer entre les gouttes plus facilement.
      </p>
      <p>
        La Suisse, de son côté, est l’un des rares pays à avoir opté très
        rapidement pour les vaccins à ARN messager, une technologie plus
        sophistiquée et – comme le prouvent les études menées jusqu’ici – plus
        efficace que les autres.
      </p>
      <p>
        Pour le professeur Flahault, la Suisse aurait toutefois dû être
        nettement plus rapide. «Il est incompréhensible qu’un pays comme la
        Suisse, qui avait les mêmes moyens qu'Israël pour se procurer rapidement
        des doses de vaccin, n'ait pas eu la même agilité.»
      </p>
    </>
  ),
  sequenced: (
    <>
      <p>
        Difficile de mesurer de manière objective la contribution scientifique
        d’un pays pour lutter contre la pandémie. Un moyen de s’en approcher est
        d’observer la proportion de tests positifs dont un séquençage ADN a été
        réalisé. Cet indicateur ne tient compte ni des publications
        scientifiques réalisées, ni de la contribution à l’élaboration de
        traitements contre la maladie ou le développement des vaccins. Cela dit,
        le taux de séquençage a l’avantage d’être disponible pour un bon nombre
        de pays.
      </p>
      <p>
        Au fil du temps, un peu comme dans un arbre généalogique, mais de façon
        beaucoup plus lente, l’ADN des virus se modifie. Il est donc possible de
        déterminer précisément où, dans l’évolution du virus, tel ou tel
        spécimen se situe.
      </p>
      <p>
        Ceci permet d’une part de mieux comprendre la propagation de la maladie,
        en retraçant la chaîne de transmission. «D’autre part – et ceci est
        surtout important pour l’avenir — on peut avoir un système d’alerte
        précoce, en cas d’émergence d’une souche n’étant pas contrecarrée par
        les différents vaccins», note la chercheuse Emma Hodcroft, l’une des
        principales contributrices à Nextstrain, une base de données mondiale
        des séquences ADN du virus.
      </p>
      <p>
        Avec environ 6% des échantillons positifs séquencés depuis le début de
        la pandémie, la Suisse se situe en troisième position, derrière le
        Danemark (37%) et la Grande-Bretagne (10%).
      </p>
      <p>
        Mais cette bonne position est surtout due à des efforts volontaires des
        laboratoires universitaires, et ce n’est qu’en mai 2021 que l’OFSP a
        décidé de soutenir financièrement le séquençage ADN à large échelle.
        «Nous l’avons longtemps fait pro-bono en quelque sorte, dit le
        professeur Didier Trono de l’EPFL. Pourtant, nous avons déjà souligné
        l’importance du séquençage l’été dernier.»
      </p>
      <p>
        En clair, la position honorable de la Suisse comparée à ses voisins
        n’est due qu’à la médiocrité de la plupart des autres. Le Danemark a
        montré ce qu’il était possible de faire.
      </p>
    </>
  ),
  gdp: (
    <>
      <p>
        Le paquet d’aide aux entreprises de la Confédération aura été l’un des
        moins bureaucratiques et des plus rapides à déployer ses effets. Il aura
        également été l’un des plus conséquents, avec plus de 60 milliards de
        francs engagés.
      </p>
      <p>
        Du coup, comparée à tous ses voisins d’Europe, la Suisse a connu la
        récession la moins forte en 2020 (-3% de PIB) et les prévisions pour
        2021 sont bonnes (+3,5% de PIB).
      </p>
      <p>
        Sur les deux années de crise, et si les prévisions pour 2021 se
        confirment, la Suisse connaîtra même une légère croissance de 0,4%.
        Seule la Suède (0,2%) se retrouve dans une situation comparable.
        L’Allemagne subira un recul de 1,5% de PIB, la France de 2,9% et
        l’Italie, la Grande-Bretagne et l’Espagne de 5%.
      </p>
      <p>
        «Du point de vue économique, la performance de la Suisse durant cette
        pandémie est exceptionnelle en Europe», souligne le professeur Antoine
        Flahault.
      </p>
    </>
  ),

  stringency: (
    <>
      <p>
        La Suisse est certainement l’un des pays qui aura le moins restreint les
        libertés individuelles en Europe. Comparé à la France et l’Italie, mais
        également par rapport à l’Allemagne et l’Autriche.
      </p>
      <p>
        Pour évaluer l’intensité des restrictions, des scientifiques de
        l’Université d’Oxford ont mis sur pied un système de mesure basé sur
        neuf indicateurs. On y trouve notamment la fermeture des lieux de
        travail ou l’obligation de télétravail, l’annulation des événements
        publics, l’interdiction des rassemblements, les restrictions de voyage,
        etc.
      </p>
      <p>
        Avec, sur l’ensemble de la pandémie, un indice moyen de restrictions de
        53 sur 100, la Suisse est restée bien plus ouverte que la France (65),
        l’Allemagne (67) ou l’Italie (72). Même la Suède, qui est peu intervenue
        lors de la première vague, a été plus rigoureuse sur toute la durée de
        la pandémie, avec un indice de 61, notamment en raison de la limitation
        plus stricte du nombre de personnes pouvant se rencontrer.
      </p>
      <p>
        Depuis février 2020 jusqu’en mai 2021, les mesures combinées appliquées
        en Suisse ont presque toujours été les moins fortes d’Europe. Seul le
        Danemark a connu une courte période entre novembre et décembre 2020 avec
        des mesures un peu plus souples que la Suisse.
      </p>
      <AutofitIFrame src='https://datawrapper.dwcdn.net/4NIWQ/' />
    </>
  ),
  schools: (
    <>
      <p>
        Le coronavirus circule autant chez les jeunes que dans le reste de la
        société. C’est désormais scientifiquement établi. Mais la Confédération
        et les Cantons ont toujours hésité à fermer les écoles, considérant que
        la fermeture devait être un ultima ratio.
      </p>
      <p>
        «En se basant sur les données disponibles, la task force scientifique a
        elle aussi soutenu cette position», raconte sa vice-présidente, la
        professeure en bioéthique Samia Hurst-Majno.
      </p>
      <p>
        Fermer les écoles n’est pas le seul moyen de protéger la santé des
        enfants et de leurs parents, précise-t-elle, et ce moyen limite aussi
        l’accès des enfants à l’éducation, d’autant plus pour les enfants de
        milieux défavorisés.
      </p>
      <p>
        Sur ce graphique, on voit que la Suisse a attendu davantage que
        l’Allemagne pour fermer les écoles, et les a rouvertes plus rapidement.
        En comparaison, l’Italie, l’Allemagne et même la France ont connu des
        fermetures bien plus longues.
      </p>
      <StepChart
        locale='FR'
        locales={{
          title: 'Fermeture des écoles et des universités',
          sub:
            'Échelle de von 0 à 3 selon le Oxford Government Response Tracker',
          options: [
            '0: Toutes les écoles ouvertes',
            '1: Faibles restrictions',
            '2: Certaines écoles fermées',
            '3: Toutes les écoles fermées'
          ]
        }}
      />
      <p>
        «Fondamentalement, un pays montre ses priorités au travers de ce qu’il
        ferme en dernier, seulement après avoir utilisé tous les autres moyens
        pour pouvoir le maintenir. En Suisse, les écoles ont fait partie de ces
        priorités», note la professeure Hurst-Majno.
      </p>
    </>
  ),

  end: (
    <>
      <h4>Le bilan final?</h4>
      <p>
        Les gouvernements ont tous demandé aux habitants de leur pays de changer
        de comportement, de faire des choix différents, de porter des fardeaux
        et de consentir des sacrifices pour le bien commun. «En échange, ils se
        sont engagés à protéger autant que possible la vie et l’intégrité
        physique des personnes contre la pandémie, mais aussi leurs droits, et
        finalement de fournir autant que possible un pont pour nous permettre
        d’arriver aussi indemnes que possible de l’autre côté sur le plan
        matériel», ajoute la bioéthicienne Samia Hurst-Majno.
      </p>
      <p>
        La Suisse semble ne pas s’en être trop mal tirée par rapport à ses
        voisins.
      </p>
      <p>
        Mais pour la professeure, un vrai bilan ne pourra toutefois se faire que
        bien plus tard. Car de nombreuses données ne sont par exemple pas encore
        disponibles. L’état économique des ménages sur le long terme, en
        particulier des plus faibles, peut être très différent de l’état de
        l’économie dans son ensemble. On ne sait pas non plus combien de
        personnes souffrent du syndrome du Covid long.
      </p>
      <p>
        D’autres éléments, comme le sentiment de cohésion nationale,
        l’impression que «mon pays a été là pour moi et moi pour lui», sont
        aussi plus difficiles à mesurer. «Une pandémie, conclut la professeure
        Hurst-Majno, est une période où il y a de multiples occasions de se
        sentir laissé pour compte, et cela peut laisser des traces.»
      </p>
    </>
  ),
  method: (
    <Box title='D’où viennent les données?'>
      <div className='sources'>
        <p>
          <b>Surmortalité </b>
          <span className='source'>
            Source:{' '}
            <Link href='https://ourworldindata.org/excess-mortality-covid'>
              Our World in Data
            </Link>
          </span>
          Nous avons pris en compte la surmortalité moyenne entre mars 2020 et
          avril 2021, en comparant le nombre effectif de décès de chaque semaine
          avec la moyenne à long terme (2015 à 2019) pour cette semaine. Pour
          l'Italie, les valeurs ne sont disponibles que jusqu'à la fin février
          2021. Ainsi, la surmortalité y est probablement surestimée dans notre
          comparaison.
        </p>
        <p>
          <b>Restrictions sanitaires</b>
          <span className='source'>
            Source:{' '}
            <Link href='https://github.com/OxCGRT/covid-policy-tracker/blob/master/documentation/codebook.md'>
              Oxford Government Response Tracker
            </Link>
          </span>
          L’Oxford Stringency Index est utilisé pour déterminer dans quelle
          mesure l'État restreint la vie publique et privée. Il tient compte,
          entre autres, des fermetures d'écoles, des couvre-feux, des
          restrictions sur les voyages internationaux et du télétravail
          obligatoire. La valeur indiquée ici est la valeur moyenne entre
          février 2020 et mai 2021. Zéro signifie aucune restriction et 100 des
          restrictions maximales.
        </p>
        <p>
          <b>Fermeture des écoles</b>
          <span className='source'>
            Source:{' '}
            <Link href='https://github.com/OxCGRT/covid-policy-tracker/blob/master/documentation/codebook.md'>
              Oxford Government Response Tracker
            </Link>
          </span>
          Les fermetures d'écoles sont également suivies dans le cadre de
          l'Oxford Stringency Index. Il s'agit de la valeur moyenne entre
          février 2020 et mai 2021. Zéro signifie qu'il n'y a aucune restriction
          au fonctionnement des écoles, 3 signifie la fermeture de tout
          enseignement en présentiel.
        </p>
        <p>
          <b>Croissance économique</b>
          <span className='source'>
            Source:{' '}
            <Link href='https://www.imf.org/external/datamapper/NGDP_RPCH@WEO/ADVEC/AUT/DEU/GBR/CHE'>
              Fonds monétaire international
            </Link>
          </span>
          Le Fonds monétaire international publie la croissance économique (en
          termes de produit intérieur brut) pour 2020, ainsi qu'une prévision
          pour 2021. La croissance combinée pour 2020 et 2021 est indiquée ici.
        </p>
        <p>
          <b>Séquençage ADN</b>
          <span className='source'>
            Source:{' '}
            <Link href='https://www.gisaid.org/index.php?id=208'>GISAID</Link>
          </span>
          Le séquençage rapide de l'ADN du plus grand nombre possible de tests
          Covid-19 positifs est important pour comprendre la propagation et
          l'évolution du virus. Les chaînes de transmission peuvent ainsi être
          tracées et les nouvelles mutations détectées à un stade précoce. Plus
          le nombre d'échantillons séquencés est élevé, plus la contribution à
          la recherche internationale est importante.
        </p>
        <p>
          <b>Vaccination</b>
          <span className='source'>
            Source:{' '}
            <Link href='https://ourworldindata.org/covid-vaccinations'>
              Vaccination
            </Link>
          </span>
          Nombre de doses de vaccin ont été administrées pour 100 personnes à la
          fin du mois de mai 2021. Cela ne permet pas de distinguer si une
          personne a reçu deux doses de vaccin ou si deux personnes en ont reçu
          une chacune. Ce chiffre est donc différent du nombre de personnes
          entièrement vaccinées ou ayant reçu la première dose.
        </p>
        <p>
          <b>Nombre de tests</b>
          <span className='source'>
            Source:{' '}
            <Link href='https://ourworldindata.org/coronavirus-testing'>
              Our World in Data
            </Link>
          </span>
          Nombre de tests effectués quotidiennement par personne à la fin du
          mois de mai. Les tests PCR et les tests antigéniques rapides sont
          inclus. Ne sont pas inclus les tests d'anticorps qui montrent si une
          personne a été exposée au virus dans le passé et les autotests qui ne
          sont pas signalés à une autorité centrale. La Suède ne rapporte que
          les tests PCR.
        </p>
      </div>
    </Box>
  )
}
